import React from "react";
import {useTranslation} from "react-i18next";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormSubSectionTitle from "./FormSubSectionTitle";
import Grid from "@mui/material/Grid";
import {useFormik} from "formik";
import IncidentSelect from "./IncidentSelect";
import GridItemOffset from "./GridItemOffset";
import IncidentField from "./IncidentField";
import Checkbox from "@mui/material/Checkbox";
import IncidentMultiSelect from "./IncidentMultiSelect";
import DialogActions from "@mui/material/DialogActions";
import MDButton from "materialDashboard/components/MDButton";
import {usePersonCaughtSchema} from "../../../schemaValidations/personCaughtSchema";
import {FieldWithTooltip} from "./FormTooltip";
import CountryAutocomplete from "../../../components/inputs/CountryAutocomplete";


const PersonCaughtDialog = ({ isOpen, onClose, onApply, person, incidentRegistry }) => {
    const {t} = useTranslation(['incidents']);
    const severityRegistry = incidentRegistry.data?.severity ?? [];
    const deathTypeRegistry = incidentRegistry.data?.deathType ?? [];
    const burialScaleRegistry = incidentRegistry.data?.burialScale ?? [];
    const ageRangeRegistry = incidentRegistry.data?.ageRange ?? [];
    const sexRegistry = incidentRegistry.data?.sex?? [];
    const rescueTypeRegistry = incidentRegistry.data?.rescueType?? [];
    const searchMethodRegistry = incidentRegistry.data?.searchMethod ?? [];
    const burialDepthRegistry = incidentRegistry.data?.burialDepth ?? [];
    const equipmentRegistry = incidentRegistry.data?.equipment ?? [];
    const jobTitleRegistry = incidentRegistry.data?.jobTitle ?? [];

    const validationSchema = usePersonCaughtSchema();

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            key: person?.key,
            id: person?.id,
            severity: person?.severity,
            deathType: person?.deathType,
            deathTypeOther: person?.deathTypeOther,
            burialScale: person?.burialScale,
            ageRange: person?.ageRange,
            age: person?.age,
            sex: person?.sex,
            nationality: person?.nationality,
            local: person?.local ?? false,
            caughtWhileWorking: person?.caughtWhileWorking ?? false,
            equipments: person?.equipments ?? [],
            equipmentOther: person?.equipmentOther,
            jobTitles: person?.jobTitles ?? [],
            jobTitleOther: person?.jobTitleOther,
            rescueType: person?.rescueType,
            searchMethod: person?.searchMethod,
            searchMethodOther: person?.searchMethodOther,
            burialDepth: person?.burialDepth,
        },
        validationSchema: validationSchema,
        onSubmit: (values, { resetForm }) => {
            onApply(values);
            resetForm();  // reset form to initial values
        },
    });
    const handleSeverityChange = async (event) => {
        // await is needed to prevent this issue https://github.com/jaredpalmer/formik/issues/2266
        const selectedSeverity = event.target.value;
        await formik.setFieldValue('severity', selectedSeverity);
        if (selectedSeverity !== "3") {
            await formik.setFieldValue('deathType', null);
        }
    }

    const handleRescueTypeChange = async (event) => {
        // await is needed to prevent this issue https://github.com/jaredpalmer/formik/issues/2266
        const selectedRescueType = event.target.value;
        await formik.setFieldValue('rescueType', selectedRescueType);
        if (selectedRescueType === "1" || selectedRescueType === "3") {
            await formik.setFieldValue('searchMethod', null);
            await formik.setFieldValue('searchMethodOther', '');
        }
    }

    return <Dialog onClose={onClose} open={isOpen} maxWidth="lg" fullWidth>
        <DialogTitle>
            {t('incidentForm.personCaughtDialog.title')}
        </DialogTitle>
        <DialogContent>
            <Grid container rowSpacing={2} columnSpacing={4}>
                <Grid item xs={12} md={4}>
                    <FormSubSectionTitle title={t('incidentForm.personCaughtDialog.severity')}/>
                </Grid>
                <Grid item xs={12} md={8}>
                    <IncidentSelect formik={formik}
                                    name="severity"
                                    onChange={handleSeverityChange}
                                    registry={severityRegistry}/>
                </Grid>

                {
                    formik.values.severity === "3" && <>
                        <GridItemOffset item xs={12} md={4}/>
                        <Grid item xs={12} md={8}>
                            <IncidentSelect formik={formik}
                                            name="deathType"
                                            registry={deathTypeRegistry}/>
                        </Grid>
                    </>
                }

                {
                    formik.values.deathType === "99" && <>
                        <GridItemOffset item xs={12} md={4}/>
                        <Grid item xs={12} md={8}>
                            <IncidentField formik={formik} name="deathTypeOther"/>
                        </Grid>
                    </>
                }

                <Grid item xs={12} md={4}>
                    <FormSubSectionTitle title={t('incidentForm.personCaughtDialog.burialScale')}/>
                </Grid>
                <Grid item xs={12} md={8}>
                    <FieldWithTooltip message={t('incidentForm.tooltip.burialScale')}>
                        <IncidentSelect formik={formik}
                                        name="burialScale"
                                        registry={burialScaleRegistry}/>
                    </FieldWithTooltip>
                </Grid>

                <Grid item xs={12} md={4}>
                    <FormSubSectionTitle title={t('incidentForm.personCaughtDialog.age')}/>
                </Grid>
                <Grid item xs={12} md={4}>
                    <IncidentSelect formik={formik}
                                    name="ageRange"
                                    registry={ageRangeRegistry}/>
                </Grid>
                <Grid item xs={12} md={4}>
                    <IncidentField formik={formik}
                                   name="age"
                                   unitOfMeasure=""
                                   type="number"
                                   registry={ageRangeRegistry}/>
                </Grid>

                <Grid item xs={12} md={4}>
                    <FormSubSectionTitle title={t('incidentForm.personCaughtDialog.sex')}/>
                </Grid>
                <Grid item xs={12} md={8}>
                    <IncidentSelect formik={formik}
                                    name="sex"
                                    registry={sexRegistry}/>
                </Grid>

                <Grid item xs={12} md={4}>
                    <FormSubSectionTitle title={t('incidentForm.personCaughtDialog.nationality')}/>
                </Grid>
                <Grid item xs={12} md={8}>
                    <CountryAutocomplete onChange={value => formik.setFieldValue("nationality", value)}
                                         value={formik.values.nationality}
                                         MDInputProps={{
                                             name: "nationality",
                                             label: t('incidentForm.nationality'),
                                             error: formik.touched.nationality && Boolean(formik.errors.nationality),
                                             helperText: formik.touched.nationality && formik.errors.nationality}
                                         }/>
                </Grid>

                <Grid item xs={12} md={4}>
                    <FormSubSectionTitle title={t('incidentForm.personCaughtDialog.local')}/>
                </Grid>
                <Grid item xs={12} md={8}>
                    <FieldWithTooltip message={t('incidentForm.tooltip.local')}>
                        <Checkbox checked={Boolean(formik.values.local)}
                                  color="info"
                                  onChange={
                                      (e) => {
                                          formik.setFieldValue("local", e.target.checked);
                                      }
                                  }/>
                    </FieldWithTooltip>
                </Grid>

                <Grid item xs={12} md={4}>
                    <FormSubSectionTitle title={t('incidentForm.personCaughtDialog.equipments')}/>
                </Grid>
                <Grid item xs={12} md={8}>
                    <IncidentMultiSelect formik={formik}
                                         name="equipments"
                                         registry={equipmentRegistry}/>
                </Grid>
                <GridItemOffset xs={12} md={4}/>
                <Grid item xs={12} md={8}>
                    <IncidentField formik={formik}
                                   name="equipmentOther"/>
                </Grid>

                <Grid item xs={12} md={4}>
                    <FormSubSectionTitle title={t('incidentForm.personCaughtDialog.jobTitle')}/>
                </Grid>
                <Grid item xs={12} md={8}>
                    <IncidentMultiSelect formik={formik}
                                         name="jobTitles"
                                         registry={jobTitleRegistry}/>
                </Grid>
                <GridItemOffset xs={12} md={4}/>
                <Grid item xs={12} md={8}>
                    <IncidentField formik={formik}
                                   name="jobTitleOther"/>
                </Grid>

                <Grid item xs={12} md={4}>
                    <FormSubSectionTitle title={t('incidentForm.personCaughtDialog.caughtWhileWorking')}/>
                </Grid>
                <Grid item xs={12} md={8}>
                    <Checkbox checked={Boolean(formik.values.caughtWhileWorking)}
                              color="info"
                              onChange={(e) => formik.setFieldValue("caughtWhileWorking", e.target.checked)}/>
                </Grid>

                <Grid item xs={12} md={4}>
                    <FormSubSectionTitle title={t('incidentForm.personCaughtDialog.rescueType')}/>
                </Grid>
                <Grid item xs={12} md={8}>
                    <FieldWithTooltip message={t('incidentForm.tooltip.rescueType')}>
                        <IncidentSelect formik={formik}
                                        name="rescueType"
                                        registry={rescueTypeRegistry}
                                        onChange={handleRescueTypeChange}/>
                    </FieldWithTooltip>
                </Grid>

                {
                    (formik.values.rescueType !== "1" && formik.values.rescueType !== "3" ) && <>
                        <Grid item xs={12} md={4}>
                            <FormSubSectionTitle title={t('incidentForm.personCaughtDialog.searchMethod')}/>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <IncidentSelect formik={formik}
                                            name="searchMethod"
                                            registry={searchMethodRegistry}/>
                        </Grid>
                        <GridItemOffset xs={12} md={4}/>
                        <Grid item xs={12} md={8}>
                            <IncidentField formik={formik}
                                           name="searchMethodOther"/>
                        </Grid>
                    </>
                }

                <Grid item xs={12} md={4}>
                    <FormSubSectionTitle title={t('incidentForm.personCaughtDialog.burialDepth')}/>
                </Grid>
                <Grid item xs={12} md={8}>
                    <FieldWithTooltip message={t('incidentForm.tooltip.burialDepth')}>
                        <IncidentSelect formik={formik}
                                        name="burialDepth"
                                        registry={burialDepthRegistry}/>
                    </FieldWithTooltip>
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <MDButton onClick={onClose}>{t('incidentForm.personCaughtDialog.cancel')}</MDButton>
            <MDButton onClick={formik.handleSubmit} color='info'>{t('incidentForm.personCaughtDialog.apply')}</MDButton>
        </DialogActions>
    </Dialog>
}

export default PersonCaughtDialog;